import React, { useState, useEffect, useCallback } from "react";
import SearchInput from "../components/CommonComponents/SearchInput";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import AddRawMaterialModal from "../components/admin/rawMaterials/AddRawMaterialModal";
import RawMaterialTable from "../components/admin/rawMaterials/RawMaterialTable";
import RawMaterialGrid from "../components/admin/rawMaterials/RawMaterialGrid";
import ViewToggle from "../components/CommonComponents/ViewToggle";
import Pagination from "../components/CommonComponents/Pagination";
import EmptyState from "../components/CommonComponents/EmptyState";
import TableLoadingSkeleton from "../components/CommonComponents/TableLoadingSkeleton";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Package } from "lucide-react";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";

const RawMaterialsList = () => {
    const { customerId } = useAuth();
    const baseURL = `${process.env.REACT_APP_API_URL}/api/admin/raw-materials`;
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`;

    // Auth header helper (copied from ProductList.js)
    const getAuthHeaders = () => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) throw new Error("Authorization token missing.");
        return { Authorization: `Bearer ${token}` };
    };
    const [materials, setMaterials] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalMaterials, setTotalMaterials] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState({ type: null, material: null, open: false });
    const [viewMode, setViewMode] = useState("table");
    const materialsPerPage = 10;

    // Fetch paginated and filtered raw materials (server-side)
    const loadMaterials = useCallback(async (page = 1, search = "") => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: materialsPerPage, customer_id: customerId };
            if (search) params.search = search;
            const res = await axios.get(baseURL, { headers: getAuthHeaders(), params });
            setMaterials(res.data.materials || res.data.data || []);
            setTotalPages(res.data.totalPages || 1);
            setTotalMaterials(res.data.total || (res.data.materials ? res.data.materials.length : 0));
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [materialsPerPage, customerId, baseURL]);

    useEffect(() => {
        loadMaterials(currentPage, searchTerm);
    }, [loadMaterials, currentPage, searchTerm]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const openModal = (type, material = null) => setModal({ type, material, open: true });
    const closeModal = () => setModal({ type: null, material: null, open: false });

    const handleDeleteMaterial = async () => {
        if (!modal.material) return;
        try {
            await axios.delete(`${baseURL}/${modal.material.id}`, { headers: getAuthHeaders() });
            setMaterials((prev) => prev.filter((m) => m.id !== modal.material.id));
            setTotalMaterials((prev) => prev - 1);
            closeModal();
            showSuccessToast("Raw material deleted successfully.");
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                // Auto-logout on auth error (optional, matches axiosInstance behavior)
                localStorage.removeItem("token");
                sessionStorage.removeItem("token");
                window.location.href = "/login";
            }
            showErrorToast(err?.response?.data?.message || "Failed to delete raw material.");
        }
    };

    const handleAddEditMaterial = async (formData, isEdit = false) => {
        try {
            if (isEdit) {
                const url = `${baseURL}/${modal.material.id}`;
                await axios.put(url, formData, {
                    headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
                });
                showSuccessToast("Raw material updated");
            } else {
                // Ensure customer_id is present in FormData
                if (formData instanceof FormData && !formData.has("customer_id")) {
                    formData.append("customer_id", customerId);
                }
                await axios.post(baseURL, formData, {
                    headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
                });
                showSuccessToast("Raw material added");
            }
            loadMaterials(currentPage, searchTerm);
            closeModal();
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                localStorage.removeItem("token");
                sessionStorage.removeItem("token");
                window.location.href = "/login";
            }
            showErrorToast(err?.response?.data?.message || (isEdit ? "Failed to update" : "Failed to add"));
        }
    };

    return (
        <div className="pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <SearchInput
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search raw materials..."
                />
                <PrimaryButton onClick={() => openModal("add")}>+ Add Raw Material</PrimaryButton>
            </div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Raw Materials</h2>
                <ViewToggle
                    viewMode={viewMode}
                    onToggle={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                />
            </div>
            {error ? (
                <EmptyState
                    icon={Package}
                    title="Failed to Load Raw Materials"
                    message="Check your internet connection and try again."
                    onRetry={loadMaterials}
                    retryLabel="Reload"
                />
            ) : loading ? (
                <div className="rounded-lg border border-gray-200 bg-white overflow-auto max-h-[calc(100vh-340px)] min-h-[300px]">
                    <TableLoadingSkeleton
                        mode={viewMode}
                        columnLabels={[
                            "Image",
                            "Code",
                            "Name",
                            "Unit",
                            "Created",
                            "Updated",
                            "Actions",
                        ]}
                        columnTypes={["image", "text", "text", "text", "text", "text", "actions"]}
                        rowCount={4}
                        gridCount={8}
                        columnTemplate="56px 1.1fr 1.6fr 0.9fr 1.1fr 1.1fr 0.9fr"
                    />
                </div>
            ) : materials.length === 0 ? (
                <EmptyState
                    icon={Package}
                    title="No Raw Materials Found"
                    message={searchTerm ? "No raw materials match your search." : "No raw materials available yet."}
                />
            ) : viewMode === "table" ? (
                <RawMaterialTable
                    materials={materials}
                    onEdit={(m) => openModal("edit", m)}
                    onDelete={(m) => openModal("delete", m)}
                    uploadsBaseURL={uploadsBaseURL}
                />
            ) : (
                <RawMaterialGrid
                    materials={materials}
                    onEdit={(m) => openModal("edit", m)}
                    onDelete={(m) => openModal("delete", m)}
                    uploadsBaseURL={uploadsBaseURL}
                />
            )}
            {!error && (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                    <p className="text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                        Showing {materials.length === 0 ? 0 : (currentPage - 1) * materialsPerPage + 1}
                        – {Math.min(currentPage * materialsPerPage, totalMaterials)}
                        of {totalMaterials} items | Page {currentPage} of {totalPages}
                    </p>
                </div>
            )}
            {modal.open && modal.type === "add" && (
                <AddRawMaterialModal editData={null} onClose={closeModal} onSubmit={handleAddEditMaterial} isOpen />
            )}
            {modal.open && modal.type === "edit" && (
                <AddRawMaterialModal editData={modal.material} onClose={closeModal} onSubmit={(data) => handleAddEditMaterial(data, true)} isOpen />
            )}
            {modal.open && modal.type === "delete" && (
                <DeleteModal
                    isOpen
                    onClose={closeModal}
                    onConfirm={handleDeleteMaterial}
                    title="Delete Raw Material"
                    message={`Are you sure you want to delete "${modal.material?.name}"?`}
                    confirmText="Yes"
                    cancelText="No"
                    type="delete"
                />
            )}
        </div>
    );
};

export default RawMaterialsList;
